import { UserClient } from "@formant/core/client/UserClient";
import { defined } from "@formant/core/common/defined";
import { isErrorType } from "@formant/core/common/isErrorType";
import { logger } from "@formant/core/common/logger";
import { ResponseError } from "@formant/core/common/ResponseError";
import { ICreateUserRequest } from "@formant/core/model/ICreateUserRequest";
import { IUser } from "@formant/core/model/IUser";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { IUserStore } from "./IUserStore";
import { StoreCache } from "./StoreCache";

export class UserStore implements IUserStore {
    private cache = new StoreCache<Uuid, IUser | false>();
    private listCache = new StoreCache<void, IUser[]>();

    constructor(
        private userClient: UserClient,
        private authStore: IAuthStore,
    ) {}

    listAll(): IUser[] | undefined {
        return this.listCache.get(
            undefined,
            async () => await this.fetchList(),
        );
    }

    listAllAsync(): Promise<IUser[]> {
        return this.listCache.getAsync(
            undefined,
            async () => (await this.fetchList()) || false,
        );
    }

    get(id: Uuid): IUser | false | undefined {
        return this.cache.get(id, async () => {
            try {
                return await this.fetchGet(id);
            } catch (err) {
                if (
                    isErrorType(
                        err,
                        ResponseError,
                        ({ statusCode }) => statusCode === 422,
                    )
                ) {
                    logger.warn(
                        "Tried to access user we don't have access to: ",
                        id,
                    );
                } else {
                    throw err;
                }
            }
            return false;
        });
    }

    authenticated(): IUser | false | undefined {
        const id = this.authStore.userId;
        if (!id) {
            return false;
        }
        return this.get(id);
    }

    async fetchGet(id: Uuid): Promise<IUser | false> {
        const user = await this.userClient.get(
            await this.authStore.token(),
            id,
        );
        const value = user || false;
        this.cache.set(id, value);
        return value;
    }

    async fetchList(): Promise<IUser[]> {
        const results = await this.userClient.list(
            await this.authStore.token(),
        );
        results.forEach(user => this.cache.set(defined(user.id), user));
        this.listCache.set(undefined, results);
        return results;
    }

    async save(user: ICreateUserRequest) {
        if (user.id === undefined) {
            return this.create(user);
        } else {
            const { password, ...updates } = user;
            return this.update(user.id, updates);
        }
    }

    async create(user: ICreateUserRequest): Promise<IUser> {
        const created = await this.userClient.create(
            await this.authStore.token(),
            {
                ...user,
                teamId: undefined,
            },
        );

        if (user.teamId && created.id) {
            await this.userClient.update(
                await this.authStore.token(),
                created.id,
                {
                    ...user,
                    teamId: user.teamId,
                    roleId: undefined,
                },
            );
        }
        this.cache.clear();
        this.listCache.clear();
        return created;
    }

    async update(id: Uuid, user: Partial<IUser>) {
        const updated = await this.userClient.update(
            await this.authStore.token(),
            id,
            user,
        );
        // Update cache with new data immediately instead of clearing
        this.cache.set(id, updated);
        this.listCache.clear();
        return updated;
    }

    async disable(id: Uuid) {
        const result = await this.update(id, {
            enabled: false,
            teamId: null,
            roleId: null,
        });
        this.cache.clear();
        this.listCache.clear();
        return result;
    }

    async addUsersToTeam(teamId: Uuid, userIds: string[]) {
        for (const userId of userIds) {
            await this.update(userId, {
                teamId: `${teamId}`,
                roleId: undefined,
            });
        }

        this.cache.clear();
        this.listCache.clear();
    }

    async removeUsersFromTeam(removedUserIds: string[]) {
        for (const userId of removedUserIds) {
            await this.update(defined(userId), {
                teamId: null,
            });
        }

        this.cache.clear();
        this.listCache.clear();
    }
}
